import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { 
  Search, 
  BarChart3, 
  Wrench, 
  ClipboardCheck, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Check
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";

const stageIcons = [Search, BarChart3, Wrench, ClipboardCheck, RefreshCw];
const stageAngles = [-90, -18, 54, 126, 198];
const stageKeys = ['identify', 'assess', 'treat', 'control', 'improve'];

export function SafetyCycle() {
  const { t } = useTranslation('home');
  const { language } = useLanguage();
  const [activeStage, setActiveStage] = useState<number>(1);
  const [viewedStages, setViewedStages] = useState<Set<number>>(new Set([1]));
  const [isPaused, setIsPaused] = useState(false);
  const [pulsingStage, setPulsingStage] = useState<number>(1);

  const stages = stageKeys.map((key, index) => ({
    id: index + 1,
    icon: stageIcons[index],
    title: t(`safetyCycle.stages.${key}.title`),
    subtitle: t(`safetyCycle.stages.${key}.subtitle`),
    description: t(`safetyCycle.stages.${key}.description`),
    angle: stageAngles[index],
    bullets: t(`safetyCycle.stages.${key}.bullets`, { returnObjects: true }) as string[],
  }));

  // Auto-pulse animation
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setPulsingStage(prev => (prev % 5) + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const handleStageClick = useCallback((stageId: number) => {
    setActiveStage(stageId);
    setViewedStages(prev => new Set([...prev, stageId]));
    setIsPaused(true);
  }, []);

  const handleNext = useCallback(() => {
    const nextId = activeStage === 5 ? 1 : activeStage + 1;
    handleStageClick(nextId);
  }, [activeStage, handleStageClick]);

  const handlePrev = useCallback(() => {
    const prevId = activeStage === 1 ? 5 : activeStage - 1;
    handleStageClick(prevId);
  }, [activeStage, handleStageClick]);

  const currentStage = stages.find(s => s.id === activeStage)!;

  return (
    <section className="relative py-12 md:py-16 lg:py-20 overflow-hidden bg-gradient-to-b from-background via-primary/5 to-background">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full bg-gradient-radial from-primary/10 via-primary/5 to-transparent blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-accent/10 blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 rounded-full bg-primary/8 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 shadow-lg shadow-accent/10">
              {t('safetyCycle.badge')}
            </span>
          </motion.div>

          <motion.h2
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            {t('safetyCycle.title')}
          </motion.h2>

          <motion.p
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {t('safetyCycle.subtitle')}
          </motion.p>
        </div>

        {/* Cycle Visualization */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-20">
          {/* Circle Container */}
          <motion.div
            className="relative w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[420px] md:h-[420px] lg:w-[480px] lg:h-[480px]"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 100 }}
          >
            {/* Outer Glow Ring */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(from 0deg, 
                  hsl(var(--primary) / 0.3), 
                  hsl(var(--accent) / 0.2), 
                  hsl(var(--primary) / 0.1), 
                  hsl(var(--accent) / 0.2), 
                  hsl(var(--primary) / 0.3))`,
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            />
            <div className="absolute inset-[3px] rounded-full bg-background" />

            {/* Center Circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-primary/30 via-primary/20 to-accent/20 border-2 border-primary/40 flex items-center justify-center shadow-2xl shadow-primary/20">
              <motion.div
                className="text-center"
                animate={{ 
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="text-primary font-bold text-base sm:text-xl md:text-2xl">{t('safetyCycle.center')}</span>
              </motion.div>
            </div>

            {/* Inner Rotating Ring */}
            <motion.div
              className="absolute inset-12 md:inset-16 rounded-full border-2 border-dashed border-primary/20"
              animate={{ rotate: -360 }}
              transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            />

            {/* Connection Lines SVG */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
              <defs>
                <linearGradient id="lineGradientActive" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="36"
                fill="none"
                stroke="url(#lineGradientActive)"
                strokeWidth="1"
                strokeDasharray="6 3"
                className="opacity-60"
              />
            </svg>

            {/* Stage Nodes */}
            {stages.map((stage, index) => {
              const angleRad = (stage.angle * Math.PI) / 180;
              const radiusPercent = 36;
              const leftPercent = 50 + Math.cos(angleRad) * radiusPercent;
              const topPercent = 50 + Math.sin(angleRad) * radiusPercent;
              const isActive = activeStage === stage.id;
              const isPulsing = pulsingStage === stage.id && !isPaused;
              const isViewed = viewedStages.has(stage.id);

              return (
                <motion.div
                  key={stage.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ 
                    left: `${leftPercent}%`,
                    top: `${topPercent}%`,
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: 0.4 + index * 0.12, 
                    duration: 0.5,
                    type: "spring",
                    stiffness: 200
                  }}
                >
                  {/* Pulse Ring Animation */}
                  <AnimatePresence>
                    {isPulsing && (
                      <motion.div
                        className="absolute inset-0 rounded-2xl border-2 border-accent"
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    )}
                  </AnimatePresence>

                  <motion.button
                    className={`relative rounded-xl sm:rounded-2xl flex flex-col items-center justify-center transition-all duration-300 cursor-pointer ${
                      isActive
                        ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-2xl shadow-primary/40"
                        : "bg-card/90 backdrop-blur-md border-2 border-border hover:border-primary/60 text-foreground hover:shadow-xl hover:shadow-primary/20"
                    }`}
                    style={{ width: 'clamp(56px, 12vw, 72px)', height: 'clamp(56px, 12vw, 72px)' }}
                    onClick={() => handleStageClick(stage.id)}
                    whileHover={{ scale: 1.15, rotate: 3 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Active Glow Effect */}
                    {isActive && (
                      <motion.div
                        className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-primary/40 to-accent/30 blur-xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        layoutId="activeGlow"
                      />
                    )}
                    
                    <stage.icon className={`w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 relative z-10 ${isActive ? "" : "text-primary"}`} />
                    <span className="text-[10px] sm:text-xs font-bold mt-0.5 sm:mt-1 relative z-10 line-clamp-1">{stage.title}</span>
                    
                    {/* Step Number Badge */}
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center transition-all ${
                      isActive 
                        ? "bg-accent text-accent-foreground shadow-lg" 
                        : isViewed 
                          ? "bg-primary/20 text-primary border border-primary/40"
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {isViewed && !isActive ? <Check className="w-3 h-3" /> : stage.id}
                    </div>
                  </motion.button>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Info Panel */}
          <motion.div
            className="w-full max-w-md lg:w-[420px] px-2 sm:px-0"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            {/* Active Stage Details Card */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStage}
                className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-7 border-2 border-primary/20 shadow-2xl shadow-primary/10"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
              >
                {/* Header */}
                <div className="flex items-center gap-4 mb-5">
                  <motion.div 
                    className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/10 flex items-center justify-center border border-primary/30"
                    whileHover={{ rotate: 10 }}
                  >
                    <currentStage.icon className="w-8 h-8 text-primary" />
                  </motion.div>
                  <div>
                    <h3 className="font-bold text-2xl text-foreground">{currentStage.title}</h3>
                    <p className="text-accent font-semibold">{currentStage.subtitle}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground leading-relaxed mb-5">
                  {currentStage.description}
                </p>

                {/* Bullets */}
                <div className="space-y-3 mb-6">
                  {currentStage.bullets.map((bullet, idx) => (
                    <motion.div
                      key={idx}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + idx * 0.1 }}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-foreground/90 text-sm">{bullet}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={handlePrev}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {language === 'he' ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    <span>{t('safetyCycle.previous')}</span>
                  </button>
                  
                  <div className="flex items-center gap-2 text-sm text-primary font-medium">
                    <span>{t('safetyCycle.stepOf', { current: currentStage.id, total: 5 })}</span>
                  </div>

                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span>{t('safetyCycle.next')}</span>
                    {language === 'he' ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                </div>

                {/* Progress Indicator */}
                <div className="flex gap-2">
                  {stages.map((stage) => (
                    <motion.button
                      key={stage.id}
                      className={`flex-1 h-2 rounded-full transition-all ${
                        stage.id === activeStage 
                          ? "bg-primary" 
                          : viewedStages.has(stage.id) 
                            ? "bg-primary/40" 
                            : "bg-muted"
                      }`}
                      onClick={() => handleStageClick(stage.id)}
                      whileHover={{ scale: 1.1 }}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* CTA */}
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Link to="/contact">
                <Button variant="hero" size="lg" className="w-full text-lg">
                  {t('safetyCycle.cta')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
